import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import type { UserDto } from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import { fetchUsers } from '../shared/api/users'
import {
  createDepartment,
  deleteDepartment,
  fetchDepartments,
  updateDepartment,
  type DepartmentRow,
} from '../shared/api/departments'

const SLUG_RE = /^[a-z0-9_-]+$/

export function AdminDepartmentsPage() {
  const { user } = useAuthStore()
  const [departments, setDepartments] = useState<DepartmentRow[]>([])
  const [users, setUsers] = useState<UserDto[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<DepartmentRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<DepartmentRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  async function reload() {
    setLoading(true)
    try {
      setDepartments(await fetchDepartments())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    fetchUsers({ active: true })
      .then(setUsers)
      .catch(() =>
        notifications.show({
          color: 'red',
          title: 'Ошибка',
          message: 'Не удалось загрузить сотрудников — выбор руководителя будет пуст',
        }),
      )
  }, [])

  if (!user) return null
  if (user.role !== 'admin') {
    return (
      <AppLayout>
        <Container>
          <Text c="red" mt="md">Доступ только для admin.</Text>
        </Container>
      </AppLayout>
    )
  }

  const userById = new Map(users.map((u) => [u.id, u]))

  async function doDelete() {
    if (!confirmDelete) return
    setDeleting(true)
    try {
      await deleteDepartment(confirmDelete.id)
      notifications.show({ message: `Отдел «${confirmDelete.name}» удалён`, color: 'green' })
      setConfirmDelete(null)
      void reload()
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        notifications.show({
          color: 'red',
          message: 'Отдел нельзя удалить: к нему привязаны сотрудники или проекты. Сначала перенесите их.',
        })
      } else {
        notifications.show({ message: extractError(e), color: 'red' })
      }
      setConfirmDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <AppLayout>
      <Container size="md" py="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>Отделы</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            Создать отдел
          </Button>
        </Group>

        {loading ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Название</Table.Th>
                <Table.Th>Slug</Table.Th>
                <Table.Th>Порядок</Table.Th>
                <Table.Th>Руководитель</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...departments].sort((a, b) => a.order - b.order).map((d) => (
                <Table.Tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setEditing(d)}>
                  <Table.Td><Text size="sm">{d.name}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{d.slug}</Text></Table.Td>
                  <Table.Td>{d.order}</Table.Td>
                  <Table.Td>{d.headUserId ? userById.get(d.headUserId)?.full_name ?? '—' : '—'}</Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(d) }}
                    >
                      Удалить
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {departments.length === 0 && (
                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="md">Отделов пока нет.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}

        <DepartmentModal
          opened={creating}
          department={null}
          users={users}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void reload() }}
        />
        <DepartmentModal
          opened={!!editing}
          department={editing}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload() }}
        />

        <Modal
          opened={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          title={confirmDelete ? `Удалить отдел «${confirmDelete.name}»?` : ''}
          centered
        >
          <Stack>
            <Text size="sm">
              Если к отделу привязаны сотрудники или проекты, удаление будет заблокировано —
              сначала перенесите их в другой отдел.
            </Text>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setConfirmDelete(null)}>Отмена</Button>
              <Button color="red" loading={deleting} onClick={doDelete}>Удалить</Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </AppLayout>
  )
}

function DepartmentModal({ opened, department, users, onClose, onSaved }: {
  opened: boolean
  department: DepartmentRow | null
  users: UserDto[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [order, setOrder] = useState<number>(0)
  const [headId, setHeadId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (opened) {
      setName(department?.name ?? '')
      setSlug(department?.slug ?? '')
      setOrder(department?.order ?? 0)
      setHeadId(department?.headUserId ?? null)
    }
  }, [opened, department])

  const slugInvalid = slug.length > 0 && !SLUG_RE.test(slug)
  const canSave = useMemo(
    () => name.trim().length >= 1 && slug.length >= 1 && !slugInvalid,
    [name, slug, slugInvalid],
  )

  async function save() {
    setSaving(true)
    try {
      const payload = { name: name.trim(), slug, order, head_user_id: headId }
      if (department) await updateDepartment(department.id, payload)
      else await createDepartment(payload)
      notifications.show({ message: department ? 'Отдел обновлён' : 'Отдел создан', color: 'green' })
      onSaved()
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      notifications.show({
        message: status === 409 ? 'Отдел с таким названием или slug уже существует.' : extractError(e),
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={department ? `Редактирование: ${department.name}` : 'Новый отдел'} centered>
      <Stack>
        <TextInput label="Название" required placeholder="Стратегия" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput
          label="Slug"
          required
          placeholder="strategy"
          description="Латиница, цифры, дефис, подчёркивание"
          error={slugInvalid ? 'Только a-z, 0-9, «-», «_»' : undefined}
          value={slug}
          onChange={(e) => setSlug(e.currentTarget.value)}
        />
        <NumberInput label="Порядок" min={0} value={order} onChange={(v) => setOrder(typeof v === 'number' ? v : 0)} />
        <Select
          label="Руководитель"
          placeholder="— нет —"
          searchable
          clearable
          data={users.map((u) => ({ value: u.id, label: u.full_name }))}
          value={headId}
          onChange={setHeadId}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Отмена</Button>
          <Button loading={saving} disabled={!canSave} onClick={save}>
            {department ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string; code?: string } } } }
  return err?.response?.data?.error?.message ?? err?.response?.data?.error?.code ?? 'Ошибка'
}
